import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { getFingerprint } from "@/lib/fingerprint";
import { Heart, ThumbsDown, Bookmark, Share2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

/** Instagram-style engagement bar with anonymous fingerprint dedupe. */
export default function EngagementBar({ quote, dense = false, onCounts }) {
  const [counts, setCounts] = useState({
    like_count: quote.like_count || 0,
    dislike_count: quote.dislike_count || 0,
    save_count: quote.save_count || 0,
    comment_count: quote.comment_count || 0,
  });
  const [mine, setMine] = useState({ like: false, dislike: false, save: false });
  const fp = typeof window !== "undefined" ? getFingerprint() : "fp_anon";

  useEffect(() => {
    let cancel = false;
    api.get(`/quotes/${quote.id}/reactions/${fp}`).then(r => {
      if (cancel) return;
      const set = new Set(r.data.reactions);
      setMine({ like: set.has("like"), dislike: set.has("dislike"), save: set.has("save") });
    }).catch(() => {});
    return () => { cancel = true; };
  }, [quote.id, fp]);

  const toggle = async (type) => {
    // optimistic
    setMine((m) => ({ ...m, [type]: !m[type], ...(type === "like" ? { dislike: false } : type === "dislike" ? { like: false } : {}) }));
    try {
      const { data } = await api.post(`/quotes/${quote.id}/react`, { reaction: type, fingerprint: fp });
      setCounts({ like_count: data.like_count, dislike_count: data.dislike_count, save_count: data.save_count, comment_count: data.comment_count });
      onCounts && onCounts(data);
    } catch { toast.error("Try again."); }
  };

  const share = async () => {
    const url = `${window.location.origin}/quote/${quote.id}`;
    const text = `"${quote.text.slice(0, 140)}${quote.text.length > 140 ? "…" : ""}" — ${quote.speaker}`;
    try {
      if (navigator.share) { await navigator.share({ title: "To Be Very Honest", text, url }); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {}
  };

  const btn = (active, count, Icon, testid, onClick, color = "#0A0A0A") => (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 ${dense ? "text-xs" : "text-sm"} font-ui`}
      data-testid={testid}
      style={{ color: active ? color : "#0A0A0A" }}
    >
      <Icon size={dense ? 14 : 18} fill={active ? color : "none"} strokeWidth={active ? 2 : 1.6} />
      <span>{count}</span>
    </button>
  );

  return (
    <div className={`flex items-center ${dense ? "gap-4" : "gap-6"}`} onClick={(e) => e.stopPropagation()} data-testid={`engagement-${quote.id}`}>
      {btn(mine.like, counts.like_count, Heart, `like-${quote.id}`, () => toggle("like"), "#FF3333")}
      {btn(mine.dislike, counts.dislike_count, ThumbsDown, `dislike-${quote.id}`, () => toggle("dislike"), "#0A0A0A")}
      {btn(false, counts.comment_count, MessageCircle, `comments-count-${quote.id}`, () => {
        const el = document.getElementById(`comments-anchor-${quote.id}`);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      })}
      {btn(mine.save, counts.save_count, Bookmark, `save-${quote.id}`, () => toggle("save"), "#0000FF")}
      <button onClick={share} className={`flex items-center gap-1.5 font-ui ${dense ? "text-xs" : "text-sm"}`} data-testid={`share-${quote.id}`}>
        <Share2 size={dense ? 14 : 18} />
      </button>
    </div>
  );
}
